import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, ShieldCheck } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { SignaturePad } from "./SignaturePad";

export const GuidelinesNotice = ({ client }) => {
  const [guidelines, setGuidelines] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [ack, setAck] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/guidelines").then((r) => setGuidelines(r.data)).catch(() => setGuidelines([]));
  useEffect(() => { load(); }, []);

  if (!guidelines) return null;

  const selectedCodes = new Set(
    ((client?.forms?.careplan?.tasks) || [])
      .filter((t) => t && (t.selected || (t.levels || []).length || t.frequency || t.instruction))
      .map((t) => String(t.code))
  );
  const required = guidelines.filter((g) => (g.task_codes || []).some((c) => selectedCodes.has(String(c))));
  const pending = required.filter((g) => !g.signed_by_me || g.needs_rereview);
  if (pending.length === 0) return null;

  const signOff = async () => {
    if (!ack) { toast.error("Please confirm you have read the guidelines"); return; }
    if (!sig) { toast.error("Please draw your signature"); return; }
    setBusy(true);
    try {
      await api.post(`/guidelines/${reviewing.id}/signoff`, { signature_png: sig });
      toast.success("Signed off — this covers you for every client whose plan includes these tasks");
      setReviewing(null);
      setAck(false);
      setSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="guidelines-blocking-modal">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        {!reviewing ? (
          <>
            <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900" data-testid="blocking-title">
              <ShieldCheck size={22} className="text-primary" /> Required training before continuing
            </p>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
              This care plan includes tasks covered by agency guidelines. You must review and sign off on each one
              before working with this client. You only sign off once — it covers you for every client whose plan
              includes these tasks.
            </p>
            <div className="mt-4 space-y-2" data-testid="blocking-list">
              {pending.map((g) => (
                <div key={g.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 bg-accent/40 p-3" data-testid={`blocking-guideline-${g.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900">{g.title}</p>
                    <p className="text-xs text-stone-500">
                      {g.needs_rereview ? "Updated since your last sign-off — re-review required" : "Review needed"}
                    </p>
                  </div>
                  <Button onClick={() => { setReviewing(g); setAck(false); setSig(null); }}
                    className="shrink-0 bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`blocking-review-${g.id}`}>
                    Review &amp; sign off
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
              <GraduationCap size={14} /> Agency guideline
            </p>
            <h2 className="mt-1 font-heading text-lg font-semibold text-stone-900">{reviewing.title}</h2>
            <p className="mt-0.5 text-xs text-stone-400">Version {reviewing.version}</p>
            <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="blocking-guideline-content">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{reviewing.content}</p>
            </div>
            <div className="mt-4 space-y-4">
              <label className="flex items-start gap-2 text-sm text-stone-700">
                <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="blocking-ack" />
                <span>I have read and understand these guidelines and agree to follow them with every client whose care plan includes these tasks.</span>
              </label>
              <SignaturePad onChange={setSig} />
              <div className="flex justify-between gap-2">
                {pending.length > 1 && (
                  <Button variant="outline" onClick={() => setReviewing(null)} className="border-stone-300 text-stone-500" data-testid="blocking-back-to-list">
                    Back to list
                  </Button>
                )}
                <Button onClick={signOff} disabled={busy} className="ml-auto bg-primary text-primary-foreground hover:bg-stone-900" data-testid="blocking-signoff-submit">
                  {busy ? "Signing..." : "Sign Off"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
